import React, { useMemo, useState } from 'react';
import { CircleX } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../Components/cleaning/Header.jsx';

const API_URL = process.env.REACT_APP_API_URL;

function useCancelSummary() {
  const location = useLocation();

  const meta = useMemo(() => {
    const params = new URLSearchParams(location.search);
    try {
      const encoded = params.get('details');
      if (!encoded) return null;

      const parsed = JSON.parse(decodeURIComponent(encoded));
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_error) {
      return null;
    }
  }, [location.search]);

  return (
    meta || {
      email: '',
      serviceType: '',
      squareFeet: '',
      rooms: '',
      bathrooms: '',
      extras: '',
      total: 0,
      bookingId: null
    }
  );
}

export default function Cancel() {
  const summary = useCancelSummary();
  const [loading, setLoading] = useState(false);

  const formatSquareFeet = (value) => {
    if (!value) return '—';
    const numeric = Number(value);
    return Number.isFinite(numeric) ? `${numeric.toLocaleString()} sq ft` : value;
  };

  const formatRooms = (value) => {
    if (!value) return '—';
    if (String(value).toLowerCase() === 'studio') return 'Studio';
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0
      ? numeric === 1
        ? '1 Room'
        : `${numeric} Rooms`
      : value;
  };

  const formatBaths = (value) => {
    if (!value) return '—';
    const numeric = Number(value);
    return Number.isFinite(numeric)
      ? numeric === 1
        ? '1 Bathroom'
        : `${numeric} Bathrooms`
      : value;
  };

  const formatExtras = (value) => {
    if (!value) return 'None';
    if (Array.isArray(value)) return value.length ? value.join(', ') : 'None';
    return value;
  };

  const formatTotal = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(numeric);
  };

  const summaryRows = [
    { label: 'Email:', value: summary.email || '—' },
    { label: 'Service Type:', value: summary.serviceType || '—' },
    { label: 'Square Feet:', value: formatSquareFeet(summary.squareFeet) },
    { label: 'Rooms:', value: formatRooms(summary.rooms) },
    { label: 'Bathrooms:', value: formatBaths(summary.bathrooms) },
    { label: 'Extra Services:', value: formatExtras(summary.extras) },
  ];

  const handleTryAgain = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const serviceKey =
        summary.serviceType.toLowerCase().includes('deep') ? 'deep' : 'basic';

      const extras =
        typeof summary.extras === 'string'
          ? summary.extras
              .split(',')
              .map((e) => e.trim().toLowerCase())
              .filter(Boolean)
          : Array.isArray(summary.extras)
          ? summary.extras.map((e) => e.toLowerCase())
          : [];

      const payload = {
        serviceKey,
        size: summary.squareFeet,
        rooms: summary.rooms,
        baths: summary.bathrooms,
        extras,
        customer: { email: summary.email },
        bookingId: summary.bookingId || null
      };

      const response = await fetch(`${API_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.id) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        alert('⚠️ Could not recreate payment session. Please try again.');
      }
    } catch (error) {
      console.error('Retry failed:', error);
      alert('Something went wrong while retrying payment.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="success-page">
      <Header showTotal={false} />
      <main className="page-shell success-page__shell cancel-shell">
        <div className="success-card card cancel-card">
          <div className="success-icon cancel-icon" aria-hidden="true">
            <CircleX />
          </div>

          <div className="success-copy">
            <h1 className="success-heading">Payment canceled</h1>
            <p className="success-subheading">
              Your booking wasn’t completed, so no charges were made. You can safely start again whenever you’re ready.
            </p>
          </div>

          <div className="success-summary card panel-cream cancel-summary">
            <h2 className="success-summary__heading">Order Summary</h2>
            <div className="payment-divider" />
            <dl className="success-summary__list">
              {summaryRows.map((row) => (
                <div key={row.label} className="success-summary__row">
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
            <div className="payment-divider" />
            <div className="success-total">
              <span className="success-total__label">Total:</span>
              <span className="success-total__value">{formatTotal(summary.total)}</span>
            </div>
          </div>

          <div className="success-actions">
            <button
              type="button"
              className="success-mail-button cancel-try-again"
              onClick={handleTryAgain}
              disabled={loading}
            >
              {loading ? 'Reconnecting…' : 'Try Again'}
            </button>
            <Link to="/" className="success-link">
              Back to booking
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
